/**
 * Match Keepspot animal metadata to IDEXX reference codes.
 *
 * The IDEXX Ordering API expects canonical codes for speciesCode / breedCode /
 * genderCode. Our local animal records carry free-form Norwegian or English
 * strings (e.g. "Hund", "Hann", "Golden retriever"). These helpers translate
 * them so the order form can pre-select the right IDEXX dropdown entry.
 *
 * Each helper takes the animal object and the live IDEXX list (fetched from
 * the cached reference endpoints) and returns the matching entry from that
 * list, or null when no confident match is found.
 *
 * Strategy:
 *   1. Consult an explicit Norwegian/English alias table first.
 *   2. Fall back to a case-insensitive substring match against the IDEXX name.
 *   3. Last resort: token-overlap match (all Keepspot words present in IDEXX name).
 */

// Alias values MUST correspond to real IDEXX codes (GET /api/v1/ref/species).
// Keys are pre-normalized (lowercase, Nordic ø/æ/å → o/ae/a).
// Keep this in sync with backend/bin/idexx-map-species-breeds.js.
const SPECIES_ALIAS = {
    // Core mammals
    dog: 'CANINE', hund: 'CANINE', canine: 'CANINE',
    cat: 'FELINE', katt: 'FELINE', feline: 'FELINE', huskatt: 'FELINE',
    horse: 'EQUINE', hest: 'EQUINE', hestehold: 'EQUINE', equine: 'EQUINE',
    donkey: 'EQUINE', esel: 'EQUINE', eselhold: 'EQUINE',
    rabbit: 'RABBIT', kanin: 'RABBIT',
    ferret: 'FERRET', ilder: 'FERRET', fretke: 'FERRET',

    // Livestock — IDEXX uses SHEEP / PIG / GOAT / BOVINE (not OVINE/PORCINE/CAPRINE)
    sheep: 'SHEEP', sau: 'SHEEP', sauehold: 'SHEEP',
    goat: 'GOAT', geit: 'GOAT', geitehold: 'GOAT',
    pig: 'PIG', gris: 'PIG', svin: 'PIG', svinehold: 'PIG',
    cow: 'BOVINE', bovine: 'BOVINE', ku: 'BOVINE', storfe: 'BOVINE', storfehold: 'BOVINE',
    reindeer: 'DEER', reinsdyr: 'DEER', deer: 'DEER', hjort: 'DEER',
    'game animal': 'GAME_ANIMAL', vilt: 'GAME_ANIMAL', viltoppdrett: 'GAME_ANIMAL',

    // Camelids
    camel: 'CAMEL', kamel: 'CAMEL', kamelhold: 'CAMEL',
    dromedar: 'CAMEL', dromedarhold: 'CAMEL',
    llama: 'LLAMA', lama: 'LLAMA', lamahold: 'LLAMA', 'lama etc': 'LLAMA',
    alpaca: 'ALPACA', alpakka: 'ALPACA', alpakkahold: 'ALPACA',

    // Rodents
    'guinea pig': 'GUINEA_PIG', marsvin: 'GUINEA_PIG',
    hamster: 'HAMSTER',
    rat: 'RAT', rotte: 'RAT',
    mouse: 'MOUSE', mus: 'MOUSE',
    gerbil: 'GERBIL',
    chinchilla: 'CHINCHILLA',
    rodent: 'RODENT', gnager: 'RODENT', gnagere: 'RODENT', smaagnager: 'RODENT',
    hedgehog: 'HEDGEHOG', pinnsvin: 'HEDGEHOG',

    // Birds
    bird: 'AVIAN', fugl: 'AVIAN', avian: 'AVIAN', burfugl: 'AVIAN',
    villfugl: 'AVIAN',
    // "Fjørfe" = poultry. Cover both ASCII spellings (fjorfe / fjoerfe).
    fjorfe: 'AVIAN', 'fjorfe andre': 'AVIAN',
    'fjorfe hoens': 'AVIAN', 'fjorfe hons': 'AVIAN',
    'fjorfe livkylling': 'AVIAN', 'fjorfe slaktekylling': 'AVIAN',
    fjoerfe: 'AVIAN', 'fjoerfe andre': 'AVIAN',
    'fjoerfe hoens': 'AVIAN', 'fjoerfe hons': 'AVIAN',
    'fjoerfe livkylling': 'AVIAN', 'fjoerfe slaktekylling': 'AVIAN',
    poultry: 'AVIAN', chicken: 'AVIAN', kylling: 'AVIAN',
    parrot: 'PARROT', papegoye: 'PARROT',
    duck: 'DUCK', and: 'DUCK', 'fjorfe and': 'DUCK', 'fjoerfe and': 'DUCK',
    goose: 'AVIAN', gaas: 'AVIAN', gas: 'AVIAN',
    'fjorfe gaas': 'AVIAN', 'fjorfe gas': 'AVIAN',
    'fjoerfe gaas': 'AVIAN', 'fjoerfe gas': 'AVIAN',
    turkey: 'TURKEY', kalkun: 'TURKEY', 'fjorfe kalkun': 'TURKEY', 'fjoerfe kalkun': 'TURKEY',
    pigeon: 'PIGEON', due: 'PIGEON',
    dove: 'DOVE',
    ostrich: 'OSTRICH', struts: 'OSTRICH',
    emu: 'EMU',

    // Reptiles / amphibians
    reptile: 'REPTILE', reptil: 'REPTILE', terrariedyr: 'REPTILE',
    amphibian: 'AMPHIBIAN', amfibie: 'AMPHIBIAN',
    snake: 'PYTHON', slange: 'PYTHON', slanger: 'PYTHON',
    python: 'PYTHON',
    boa: 'BOA',
    turtle: 'TURTLE', skilpadde: 'TURTLE', skilpadder: 'TURTLE',
    tortoise: 'LEOPARD_TORTOISE',
    iguana: 'IGUANA', legua: 'IGUANA',
    lizard: 'MONITOR_LIZARD', ogler: 'MONITOR_LIZARD', ogle: 'MONITOR_LIZARD',

    // Fish
    fish: 'FISH', fisk: 'FISH', oppdrettsfisk: 'FISH',

    // Zoo / exotic / "other"
    'zoo animal': 'MAMMAL_ZOO', 'zoo dyr': 'MAMMAL_ZOO', zoodyr: 'MAMMAL_ZOO',
    primate: 'PRIMATE',
    marsupial: 'MARSUPIAL',
    bear: 'BEAR', bjorn: 'BEAR',
    tiger: 'TIGER',
    lion: 'LION', lowe: 'LION',
    dolphin: 'DOLPHIN', delfin: 'DOLPHIN',
    elephant: 'ELEPHANT', elefant: 'ELEPHANT',
    kangaroo: 'KANGAROO', kenguru: 'KANGAROO',
    wallaby: 'WALLABY',
    cheetah: 'CHEETAH', gepard: 'CHEETAH',
    pelsdyr: 'OTHER',

    // Fallback
    other: 'OTHER', annet: 'OTHER', andre: 'OTHER', 'andre dyr': 'OTHER',
    'andre matproduserende dyr': 'OTHER',
    unknown: 'OTHER', ukjent: 'OTHER',
    'test species no': 'OTHER', 'test species': 'OTHER',
};

// Lowercase → IDEXX gender code. Keepspot genders are entered freehand, so
// we cover the common spellings in both languages plus their short forms.
const GENDER_ALIAS = {
    male: 'MALE_INTACT',
    m: 'MALE_INTACT',
    mann: 'MALE_INTACT',
    hann: 'MALE_INTACT',
    han: 'MALE_INTACT',
    'male intact': 'MALE_INTACT',

    female: 'FEMALE_INTACT',
    f: 'FEMALE_INTACT',
    kvinne: 'FEMALE_INTACT',
    hunn: 'FEMALE_INTACT',
    'female intact': 'FEMALE_INTACT',

    'male neutered': 'MALE_NEUTERED',
    neutered: 'MALE_NEUTERED',
    castrated: 'MALE_NEUTERED',
    kastrert: 'MALE_NEUTERED',
    'kastrert hann': 'MALE_NEUTERED',
    'hann kastrert': 'MALE_NEUTERED',

    'female spayed': 'FEMALE_SPAYED',
    spayed: 'FEMALE_SPAYED',
    sterilisert: 'FEMALE_SPAYED',
    'sterilisert hunn': 'FEMALE_SPAYED',
    'hunn sterilisert': 'FEMALE_SPAYED',

    unknown: 'UNKNOWN',
    ukjent: 'UNKNOWN',
    u: 'UNKNOWN',
};

// Breed aliases — keep in sync with backend/bin/idexx-map-species-breeds.js.
// Keys are norm()ed (ASCII, Nordic folded). Values are real IDEXX codes.
const BREED_ALIAS = {
    // Mixed / other (canine)
    blandingshund: 'MIXED_BREED_CANINE',
    blanding: 'MIXED_BREED_CANINE',
    blandingsrase: 'MIXED_BREED_CANINE',
    'ikke godkjent hunderase': 'MIXED_BREED_CANINE',
    hundbreed: 'CANINE_OTHER',
    // Mixed / other (feline)
    blandingskatt: 'MIXED_BREED_FELINE',
    'ikke godkjent katterase': 'MIXED_BREED_FELINE',
    // Domestic cats
    huskatt: 'DOMESTIC_SHORTHAIR',
    'norsk huskatt': 'DOMESTIC_SHORTHAIR',
    'huskatt korthar': 'DOMESTIC_SHORTHAIR',
    'huskatt langhar': 'DOMESTIC_LONGHAIR',
    // Cat breeds (Norwegian -er suffix)
    abyssiner: 'ABYSSINIAN_FELINE',
    perser: 'PERSIAN',
    siameser: 'SIAMESE',
    burmeser: 'BURMESE',
    balineser: 'BALINESE',
    javaneser: 'JAVANESE',
    orientaler: 'ORIENTAL_LONGHAIR',
    sibirkatt: 'SIBERIAN',
    'hellig birma': 'BIRMAN',
    'norsk skogkatt': 'NORWEGIAN_FOREST_CAT',
    // Dog breed Norwegianisations
    dalmatiner: 'DALMATION', // IDEXX spells it "DALMATION" (sic)
    faraohund: 'HOUND_PHARAOH',
    'grand danois': 'GREAT_DANE',
    'norsk elghund': 'NORWEGIAN_ELKHOUND',
    'norsk lundehund': 'NORWEGIAN_LUNDEHUND',
    grosspitz: 'GERMAN_SPITZ',
    kleinspitz: 'GERMAN_SPITZ',
    mittelspitz: 'GERMAN_SPITZ',
    dvergpinscher: 'PINSCHER_MINIATURE',
    dvergschnauzer: 'SCHNAUZER_MINIATURE',
    dvergpuddel: 'POODLE',
    'toy puddel': 'POODLE',
    toypuddel: 'POODLE',
    'stor puddel': 'POODLE',
    storpuddel: 'POODLE',
    puddel: 'POODLE',
    // Fallback
    ukjent: 'CANINE_OTHER',
    annet: 'CANINE_OTHER',
};

// Norwegian → English token substitution applied before matching. Keys are
// norm()ed. Values may be multi-word. Keep in sync with backend script.
const NO_EN_TOKENS = {
    engelsk: 'english', tysk: 'german', fransk: 'french',
    italiensk: 'italian', irsk: 'irish', spansk: 'spanish',
    skotsk: 'scottish', svensk: 'swedish', finsk: 'finnish',
    dansk: 'danish', tyrkisk: 'turkish', norsk: 'norwegian',
    japansk: 'japanese', russisk: 'russian', amerikansk: 'american',
    hollandsk: 'dutch', kroatisk: 'croatian', ungarsk: 'hungarian',
    tsjekkisk: 'czech', belgisk: 'belgian', australsk: 'australian',
    meksikansk: 'mexican', egyptisk: 'egyptian', estisk: 'estonian',
    slovensk: 'slovenian', slovakisk: 'slovak', afrikansk: 'african',
    afghansk: 'afghan', brasiliansk: 'brazilian', brasialansk: 'brazilian',
    islandsk: 'icelandic', koreansk: 'korean', anatolsk: 'anatolian',
    sveitsisk: 'swiss', polsk: 'polish', portugisisk: 'portuguese',
    tsjekkoslovakisk: 'czechoslovakian', serbisk: 'serbian',
    bosnisk: 'bosnian', gresk: 'greek',
    osterriksk: 'austrian', ostersiksk: 'austrian',
    katalansk: 'catalan', balkansk: 'balkan', balkan: 'balkan',
    hund: 'dog', katt: 'cat',
    stover: 'hound', mynde: 'greyhound',
    gjeterhund: 'shepherd', farehund: 'shepherd',
    spisshund: 'spitz', sennenhund: 'mountain dog',
    ulvehund: 'wolfhound', vannhund: 'water dog',
    vannspaniel: 'water spaniel', elghund: 'elkhound',
    viltsporhund: 'hound', bjornehund: 'bear dog', bjornhund: 'bear dog',
    dogge: 'mastiff', nakenhund: 'hairless',
    nakenterrier: 'hairless terrier', bracke: 'hound',
    korthar: 'shorthair', langhar: 'longhair',
    kortharet: 'shorthaired', langharet: 'longhaired',
    strihar: 'wirehaired', strihared: 'wirehaired', striharet: 'wirehaired',
    ruhar: 'wirehaired', ruharet: 'wirehaired',
    dverg: 'miniature', stor: 'standard', toy: 'toy', liten: 'little',
};

const STOPWORD_TOKENS = new Set(['a', 'i', 'o', 'e', 'of', 'the', 'and', 'de', 'en', 'la', 'le', 'et', 'och']);

function norm(str) {
    return (str || '')
        .toString()
        .toLowerCase()
        // Fold Nordic letters to ASCII so "Øgler" and "ogler" compare equal,
        // and the alias keys (ASCII) match Norwegian inputs like "sauehold".
        .replace(/ø/g, 'o')
        .replace(/æ/g, 'ae')
        .replace(/å/g, 'a')
        // Strip combining accents (é → e, etc.)
        .normalize('NFD').replace(/[̀-ͯ]/g, '')
        .trim()
        .replace(/[,./()[\]_-]/g, ' ')
        .replace(/\s+/g, ' ');
}

function tokenize(str) {
    return norm(str).split(' ').filter(Boolean);
}

function findByCode(list, code) {
    if (!code || !Array.isArray(list)) return null;
    return list.find(x => x && x.code === code) || null;
}

// Species-aware fallbacks for generic Norwegian terms ("Annet" / "Ukjent" /
// "Blanding*"). Try each code in order — return whichever is in the list.
const SPECIES_AWARE_FALLBACKS = {
    annet: ['CANINE_OTHER', 'FELINE_OTHER', 'OTHER_BREED', 'OTHER'],
    ukjent: ['CANINE_OTHER', 'FELINE_OTHER', 'OTHER_BREED', 'OTHER'],
    blanding: ['MIXED_BREED_CANINE', 'MIXED_BREED_FELINE'],
    blandingsrase: ['MIXED_BREED_CANINE', 'MIXED_BREED_FELINE'],
    blandingshund: ['MIXED_BREED_CANINE'],
    blandingskatt: ['MIXED_BREED_FELINE'],
    'i a': ['MIXED_BREED_CANINE', 'MIXED_BREED_FELINE', 'CANINE_OTHER', 'FELINE_OTHER'],
};

function resolveFallback(name, list) {
    const n = norm(name);
    if (!n || !Array.isArray(list)) return null;
    if (/^blanding/.test(n)) {
        return list.find(x => x.code === 'MIXED_BREED_CANINE')
            || list.find(x => x.code === 'MIXED_BREED_FELINE')
            || null;
    }
    const codes = SPECIES_AWARE_FALLBACKS[n];
    if (codes) {
        for (const code of codes) {
            const hit = list.find(x => x.code === code);
            if (hit) return hit;
        }
    }
    return null;
}

function translateNorwegian(str) {
    const toks = tokenize(str);
    if (toks.length === 0) return '';
    const out = [];
    for (const t of toks) {
        const mapped = NO_EN_TOKENS[t];
        if (mapped) out.push(...mapped.split(' '));
        else out.push(t);
    }
    return out.join(' ').replace(/\s+/g, ' ').trim();
}

function findByName(list, query) {
    if (!query || !Array.isArray(list)) return null;
    const variants = [];
    const q1 = norm(query);
    const q2 = translateNorwegian(query);
    if (q1) variants.push(q1);
    if (q2 && !variants.includes(q2)) variants.push(q2);
    if (variants.length === 0) return null;

    for (const q of variants) {
        // 1. Exact match on name or code
        const exact = list.find(x => norm(x.name) === q || norm(x.code) === q);
        if (exact) return exact;

        // 2. Substring either direction, name or code
        const substr = list.find(x => {
            const n = norm(x.name);
            const c = norm(x.code);
            return (n && (n.includes(q) || q.includes(n)))
                || (c && (c.includes(q) || q.includes(c)));
        });
        if (substr) return substr;

        // 3. Token overlap — every meaningful local token must match a
        // candidate token exactly or via a ≥4-char shared prefix. Stopwords
        // (i, a, o, and, etc.) are ignored.
        const qTokens = tokenize(q).filter(t => t.length >= 3 && !STOPWORD_TOKENS.has(t));
        if (qTokens.length === 0) continue;
        const tokenMatch = list.find(x => {
            const candTokens = new Set([...tokenize(x.name), ...tokenize(x.code)]);
            return qTokens.every(lt => {
                for (const ct of candTokens) {
                    if (ct === lt) return true;
                    if (lt.length >= 4 && ct.length >= 4) {
                        if (ct.startsWith(lt) || lt.startsWith(ct)) return true;
                    }
                }
                return false;
            });
        });
        if (tokenMatch) return tokenMatch;
    }
    return null;
}

/**
 * Match animal.species (Norwegian or English text) to an IDEXX species entry.
 * Returns the matching { code, name } from speciesList or null.
 *
 * `explicitCode` takes priority: pass the hard-linked `animalSpecies.idexxCode`
 * stored in the local DB (resolved via GET /api/integrations/idexx/resolve-codes).
 * When provided and it resolves to an entry in speciesList, it wins — no fuzzy
 * fallback. This is the reliable path; only use the alias + name matcher when
 * the species record has no idexxCode set.
 */
export function matchIdexxSpecies(animal, speciesList, explicitCode) {
    if (!Array.isArray(speciesList) || speciesList.length === 0) return null;

    if (explicitCode) {
        const byCode = findByCode(speciesList, explicitCode);
        if (byCode) return byCode;
    }

    if (!animal) return null;
    const raw = animal.species || animal.animalSpecies;
    if (!raw) return null;

    const fallback = resolveFallback(raw, speciesList);
    if (fallback) return fallback;

    const code = SPECIES_ALIAS[norm(raw)];
    if (code) {
        const byCode = findByCode(speciesList, code);
        if (byCode) return byCode;
    }
    return findByName(speciesList, raw);
}

/**
 * Match animal.breed to an IDEXX breed entry within the given speciesCode's list.
 * breedList is expected to already be filtered by species (the backend does
 * this when the route receives ?speciesCode=X).
 *
 * `explicitCode` takes priority — pass the hard-linked `animalBreed.idexxCode`.
 */
export function matchIdexxBreed(animal, breedList, explicitCode) {
    if (!Array.isArray(breedList) || breedList.length === 0) return null;

    if (explicitCode) {
        const byCode = findByCode(breedList, explicitCode);
        if (byCode) return byCode;
    }

    if (!animal) return null;
    const raw = animal.breed || animal.animalBreed;
    if (!raw) return null;

    const fallback = resolveFallback(raw, breedList);
    if (fallback) return fallback;

    const code = BREED_ALIAS[norm(raw)];
    if (code) {
        const byCode = findByCode(breedList, code);
        if (byCode) return byCode;
    }
    return findByName(breedList, raw);
}

/**
 * Match animal.gender to an IDEXX gender entry. Supports Norwegian aliases
 * like "Hann", "kastrert", "sterilisert hunn" as well as short forms (M/F/U).
 * No hard-link mapping — gender is always derived from the animal record
 * itself, since there's no separate gender collection in the DB.
 */
export function matchIdexxGender(animal, genderList) {
    if (!animal || !Array.isArray(genderList) || genderList.length === 0) return null;
    const raw = animal.gender || animal.sex;
    if (!raw) return null;

    const code = GENDER_ALIAS[norm(raw)];
    if (code) {
        const byCode = findByCode(genderList, code);
        if (byCode) return byCode;
    }
    return findByName(genderList, raw);
}

export default {
    matchIdexxSpecies,
    matchIdexxBreed,
    matchIdexxGender,
};
